/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * Selectors for widget Personal Account
 *
 * @author Yakov Gushchin
 * @author Misha Kokoulin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Julia Ignatieva
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.0.1
 */

import { createSelector, OutputSelector } from 'reselect';

import { IRootReducer } from '../../../../../reducers/rootReducer';
import { EAccountType } from '../../../../PageAuth/constants/authConstants';
import { EWidgetCommonStatus } from '../widgetTypes';
import { IWidgetPersonalAccountCashbackUrl, IWidgetPersonalAccountReducer } from './types/widgetPersonalAccountType';

export const makeSelectWidgetPersonalAccount = (state: IRootReducer): IWidgetPersonalAccountReducer =>
    state.dashboard.widgetPersonalAccount;

export const makeSelectWidgetPersonalAccountNumber = ():
    OutputSelector<IRootReducer, string, (state: IWidgetPersonalAccountReducer) => string> =>
    createSelector(
        makeSelectWidgetPersonalAccount,
        (state: IWidgetPersonalAccountReducer) => state.data.accountNumber,
    );

export const makeSelectWidgetPersonalAccountBalanceDate = ():
    OutputSelector<IRootReducer, string, (state: IWidgetPersonalAccountReducer) => string> =>
    createSelector(
        makeSelectWidgetPersonalAccount,
        (state: IWidgetPersonalAccountReducer) => state.data.balanceDate,
    );

export const makeSelectWidgetPersonalAccountCashbackAmount = ():
    OutputSelector<IRootReducer, number, (state: IWidgetPersonalAccountReducer) => number> =>
    createSelector(
        makeSelectWidgetPersonalAccount,
        (state: IWidgetPersonalAccountReducer) => state.data.cashbackAmount,
    );

export const makeSelectWidgetPersonalAccountCreditBalance = ():
    OutputSelector<IRootReducer, number | undefined, (state: IWidgetPersonalAccountReducer) => number | undefined> =>
    createSelector(
        makeSelectWidgetPersonalAccount,
        (state: IWidgetPersonalAccountReducer) => state.data.creditLimit,
    );

export const makeSelectWidgetPersonalAccountCurrentBalance = ():
    OutputSelector<IRootReducer, number, (state: IWidgetPersonalAccountReducer) => number> =>
    createSelector(
        makeSelectWidgetPersonalAccount,
        (state: IWidgetPersonalAccountReducer) => state.data.currentBalance,
    );

export const makeSelectWidgetPersonalAccountTotalBalance = ():
    OutputSelector<IRootReducer, number | undefined, (state: IWidgetPersonalAccountReducer) => number | undefined> =>
    createSelector(
        makeSelectWidgetPersonalAccount,
        (state: IWidgetPersonalAccountReducer) => state.data.totalBalance,
    );

export const makeSelectWidgetPersonalAccountNearestPayDate = ():
    OutputSelector<IRootReducer, string | undefined, (state: IWidgetPersonalAccountReducer) => string | undefined> =>
    createSelector(
        makeSelectWidgetPersonalAccount,
        (state: IWidgetPersonalAccountReducer) => state.data.nearestPayDate,
    );

export const makeSelectWidgetPersonalAccountCashbackUrl = ():
    OutputSelector<IRootReducer, IWidgetPersonalAccountCashbackUrl,
        (state: IWidgetPersonalAccountReducer) =>
            IWidgetPersonalAccountCashbackUrl> => createSelector(
    makeSelectWidgetPersonalAccount,
    (state: IWidgetPersonalAccountReducer) => ({
        urlHowToGetCashback: state.data.urlHowToGetCashback,
        urlHowToSpentCashback: state.data.urlHowToSpentCashback,
    }),
);

export const makeSelectWidgetPersonalAccountOutflowType = ():
    OutputSelector<IRootReducer, EAccountType, (state: IWidgetPersonalAccountReducer) => EAccountType> =>
    createSelector(
        makeSelectWidgetPersonalAccount,
        (state: IWidgetPersonalAccountReducer) => state.data.outflowType,
    );

export const makeSelectWidgetPersonalAccountUnpaidBillsAmount = ():
    OutputSelector<IRootReducer, number | undefined, (state: IWidgetPersonalAccountReducer) => number | undefined> =>
    createSelector(
        makeSelectWidgetPersonalAccount,
        (state: IWidgetPersonalAccountReducer) => state.data.unpaidBillsAmount,
    );

export const makeSelectWidgetPersonalAccountUnpaidBillsQuantity = ():
    OutputSelector<IRootReducer, number | undefined, (state: IWidgetPersonalAccountReducer) => number | undefined> =>
    createSelector(
        makeSelectWidgetPersonalAccount,
        (state: IWidgetPersonalAccountReducer) => state.data.unpaidBillsQuantity,
    );

export const makeSelectorWidgetPersonalAccountStatus = ():
    OutputSelector<IRootReducer, EWidgetCommonStatus, (state: IWidgetPersonalAccountReducer) => EWidgetCommonStatus> =>
    createSelector(
        makeSelectWidgetPersonalAccount,
        (state: IWidgetPersonalAccountReducer) => state.error
            ? EWidgetCommonStatus.ERROR
            : EWidgetCommonStatus.SUCCESS,
    );

export const makeSelectWidgetPersonalAccountIsLoading = ():
    OutputSelector<IRootReducer, boolean, (state: IWidgetPersonalAccountReducer) => boolean> =>
    createSelector(
        makeSelectWidgetPersonalAccount,
        (state: IWidgetPersonalAccountReducer) => state.isLoading,
    );

export const makeSelectWidgetPersonalAccountPromisedPaymentAmount = ():
    OutputSelector<IRootReducer, number, (state: IWidgetPersonalAccountReducer) => number> =>
    createSelector(makeSelectWidgetPersonalAccount,
        state => state.data.activePromisedPaymentAmount || 0);

export const makeSelectWidgetPersonalAccountPromisedPaymentEndDate = ():
    OutputSelector<IRootReducer, string, (state: IWidgetPersonalAccountReducer) => string> =>
    createSelector(makeSelectWidgetPersonalAccount,
        state => state.data.activePromisedPaymentEndDate || '');

export const makeSelectWidgetPersonalAccountPromisedPaymentButton = ():
    OutputSelector<IRootReducer, boolean, (state: IWidgetPersonalAccountReducer) => boolean> =>
    createSelector(makeSelectWidgetPersonalAccount,
        state => !!state.data.promisedPaymentButtonAvailable);

export const makeSelectWidgetPersonalAccountHasNewBills = ():
    OutputSelector<IRootReducer, boolean, (state: IWidgetPersonalAccountReducer) => boolean> =>
    createSelector(makeSelectWidgetPersonalAccount,
        state => !!state.data.hasNewBills);
